import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { CONTACT, NAV } from "@/content";
import { InquiryForm } from "@/components/site/InquiryForm";
import { MaskLines, Overline, Reveal } from "@/components/site/Reveal";
import { SectionBackdrop } from "@/components/site/BgFade";
import { BrandImg } from "@/components/site/Brand";
import { scrollToId } from "@/hooks/useLenis";

const ContactRow = ({ icon: Icon, label, value, href, testId }) => (
  <a href={href} className="group flex items-start gap-4 border-b border-rc-line py-5" data-testid={testId}>
    <Icon className="mt-1 h-4 w-4 text-rc-copper" />
    <span>
      <span className="block font-mono-rc text-[11px] uppercase tracking-[0.22em] text-rc-muted">{label}</span>
      <span className="mt-1 block text-lg text-rc-ink transition-colors group-hover:text-rc-copper">{value}</span>
    </span>
  </a>
);

export const Inquire = () => (
  <section id="inquire" className="relative overflow-hidden bg-rc-bg2 py-28 lg:py-40" data-testid="inquire-section">
    <SectionBackdrop photos={["/images/couple-window.webp", "/images/couple-sofa.webp"]} testId="inquire-bg" />
    <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-12">
      <div className="lg:col-span-5">
        <Overline>Contact &amp; Inquiries</Overline>
        <MaskLines
          lines={["Tell us about", <>your <em className="italic text-rc-copper">vision.</em></>]}
          className="mt-8 font-display text-4xl font-medium leading-[1.02] tracking-tight text-rc-ink sm:text-5xl lg:text-6xl"
        />
        <Reveal className="mt-8 max-w-md text-base leading-relaxed text-rc-body">
          Share a few details and we'll recommend the right services and provide pricing.
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <ContactRow icon={Mail} label="Email" value={CONTACT.email} href={`mailto:${CONTACT.email}`} testId="contact-email" />
          <ContactRow icon={Phone} label="Phone" value={CONTACT.phone} href={CONTACT.phoneHref} testId="contact-phone" />
          <div className="flex items-start gap-4 border-b border-rc-line py-5" data-testid="contact-location">
            <MapPin className="mt-1 h-4 w-4 text-rc-copper" />
            <span>
              <span className="block font-mono-rc text-[11px] uppercase tracking-[0.22em] text-rc-muted">Based in</span>
              <span className="mt-1 block text-lg text-rc-ink">{CONTACT.location}</span>
              <span className="mt-1 block text-sm text-rc-muted">Serving the Carolinas and clients nationwide</span>
            </span>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="lg:col-span-7">
        <div className="rounded-[28px] border border-rc-line bg-rc-surface/40 p-6 backdrop-blur-[2px] sm:p-10">
          <InquiryForm />
        </div>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="relative overflow-hidden pt-24" data-testid="site-footer">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <p className="font-display text-3xl leading-snug text-rc-ink sm:text-4xl">
            Creative excellence. <em className="italic text-rc-copper">Purposeful</em> leadership.
          </p>
        </div>
        <div className="md:col-span-3">
          <Overline>Explore</Overline>
          <ul className="mt-5 space-y-3">
            {NAV.map((n) => (
              <li key={n.id}>
                <button onClick={() => scrollToId(n.id)} className="text-rc-body transition-colors hover:text-rc-copper" data-testid={`footer-link-${n.id}`}>{n.label}</button>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <Overline>Follow</Overline>
          <ul className="mt-5 space-y-3">
            {CONTACT.socials.map((s) => (
              <li key={s.handle}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-rc-body transition-colors hover:text-rc-copper" data-testid={`footer-social-${s.handle.replace(/[@_]/g, "")}`}>
                  <Instagram className="h-4 w-4" /> {s.handle} <span className="text-rc-faint">· {s.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <BrandImg kind="rco" alt="The Rhett Co" className="mt-20 w-20 select-none sm:w-24" data-testid="footer-logo" />
      <div className="mt-10 flex flex-col gap-3 border-t border-rc-line py-8 font-mono-rc text-[11px] uppercase tracking-[0.2em] text-rc-muted sm:flex-row sm:justify-between">
        <span>© {new Date().getFullYear()} The Rhett Co LLC</span>
        <span>Charlotte, NC · {CONTACT.website}</span>
      </div>
    </div>
  </footer>
);
